/**
 * App.tsx — Root application component for Chanelle OS.
 *
 * Responsibilities:
 *   1. Initialise the monday SDK and listen for context
 *   2. Detect the app surface (custom_object | dashboard_widget | unknown)
 *   3. Render the correct surface (full tab shell vs single widget)
 *   4. Provide React Query client + Zustand store to the entire tree
 *   5. Mount the Command Palette overlay (always present, opened via CMD+K)
 *   6. Apply theme tokens from the monday SDK context
 */

import React, { useEffect, useState, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { NavBar } from './app/navigation/NavBar';
import { HomeTab } from './app/tabs/HomeTab';
import { MyWorkTab } from './app/tabs/MyWorkTab';
import { InboxTab } from './app/tabs/InboxTab';
import { VaultTab } from './app/tabs/VaultTab';
import { MonitorTab } from './app/tabs/MonitorTab';
import { CalendarTab } from './app/tabs/CalendarTab';
import { AnalyticsTab } from './app/tabs/AnalyticsTab';
import { SettingsTab } from './app/tabs/SettingsTab';
import { AdminPanel } from './app/tabs/Admin/AdminPanel';

import { CommandPalette } from './components/CommandPalette';
import { WidgetRenderer } from './widget-framework/WidgetRenderer';

import { useAppStore } from './core/store/appStore';
import { loadBoardIds, discoverBoardsByName } from './core/utils/boardSetup';
import { storageSetJSON, storageGetJSON } from './core/api/client';
import { STORAGE_KEYS, FEED_BOARD_IDS } from './core/utils/defaults';
import { registerAllKnownSources } from './core/utils/feedBoardSetup';

import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── React Query Client ──────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,    // 2 minutes
      gcTime: 10 * 60 * 1000,       // 10 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// ─── Surface Detection ───────────────────────────────────────────────────────

type AppSurface = 'custom_object' | 'dashboard_widget' | 'loading';

function detectSurface(context: Record<string, unknown>): AppSurface {
  // monday SDK context nests feature info under different paths depending on
  // the app version.  Try a few known locations:
  const appCtx = context?.app as Record<string, unknown> | undefined;
  const featureType =
    (appCtx?.features as Record<string, unknown>)?.type as string | undefined ??
    (appCtx as Record<string, unknown>)?.featureType as string | undefined;

  console.log('[ChanelleOS] detectSurface — featureType:', featureType);

  if (featureType === 'AppFeatureDashboardWidget') return 'dashboard_widget';

  // Board Views, Custom Object, or any other surface → render the full tab shell
  // (AppFeatureBoardViewFeature, AppFeatureCustomObjectFeature, etc.)
  return 'custom_object';
}

// ─── Tab Map ─────────────────────────────────────────────────────────────────

const TAB_COMPONENTS: Record<string, React.FC> = {
  home:      HomeTab,
  my_work:   MyWorkTab,
  inbox:     InboxTab,
  vault:     VaultTab,
  monitor:   MonitorTab,
  calendar:  CalendarTab,
  analytics: AnalyticsTab,
  settings:  SettingsTab,
  admin:     AdminPanel,
};

// ─── Board ID Initialization ─────────────────────────────────────────────────

/**
 * Load or discover board IDs and push them into the Zustand store.
 *
 * 1. Skip if board IDs are already populated (from Zustand localStorage cache).
 * 2. Try loading from monday Storage (persisted on a previous session).
 * 3. Fall back to auto-discovering boards by name pattern ("COS — *").
 */
/**
 * One-time migration: purge stale layout keys from monday Storage that
 * still reference the deprecated `mini_calendar` widget. After purge,
 * set a flag so it only runs once.
 */
const LAYOUT_MIGRATION_KEY = 'cos_layout_migration_v3';
async function runLayoutMigration(userId: string): Promise<void> {
  try {
    const flag = await storageGetJSON<boolean>(LAYOUT_MIGRATION_KEY);
    if (flag) return; // Already migrated
    console.log('[ChanelleOS] Running one-time layout migration (mini_calendar → google_calendar)…');

    const tabIds = ['home', 'my_work', 'inbox', 'vault', 'monitor', 'calendar', 'analytics', 'settings', 'admin'];
    for (const tabId of tabIds) {
      const key = STORAGE_KEYS.layout(userId, tabId);
      // Delete the stored layout so useTabLayout falls back to preset defaults
      await monday.storage.instance.deleteItem(key).catch(() => null);
    }

    await storageSetJSON(LAYOUT_MIGRATION_KEY, true);
    console.log('[ChanelleOS] Layout migration complete — fresh defaults will load');
  } catch (err) {
    console.warn('[ChanelleOS] Layout migration failed:', err);
  }
}

async function initializeBoardIds(userId: string): Promise<void> {
  const store = useAppStore.getState();

  // Already loaded (e.g. Zustand persist restored from localStorage)
  if (store.boardIds?.actions && store.boardIds?.topics && store.boardIds?.notes) {
    console.log('[ChanelleOS] Board IDs already loaded from cache');
    return;
  }

  // Phase 1: Try monday Storage
  try {
    const stored = await loadBoardIds(userId);
    if (stored && stored.actions) {
      console.log('[ChanelleOS] Board IDs loaded from Storage:', stored);
      useAppStore.getState().setBoardIds(stored);
      return;
    }
  } catch (err) {
    console.warn('[ChanelleOS] loadBoardIds from Storage failed:', err);
  }

  // Phase 2: Auto-discover by board name
  console.log('[ChanelleOS] No stored board IDs — attempting auto-discovery…');
  try {
    const discovered = await discoverBoardsByName();
    if (discovered && discovered.actions) {
      useAppStore.getState().setBoardIds(discovered);
      // Persist for next session
      const key = STORAGE_KEYS.boardIds(userId);
      await storageSetJSON(key, discovered).catch(() => null);
      console.log('[ChanelleOS] Board IDs discovered and saved:', discovered);
    } else {
      console.warn('[ChanelleOS] Auto-discovery found no matching boards');
    }
  } catch (err) {
    console.warn('[ChanelleOS] Board auto-discovery failed:', err);
  }
}

// ─── Inner App (requires QueryClient in tree) ─────────────────────────────────

function AppInner() {
  const { activeTab } = useAppStore();
  const [surface, setSurface] = useState<AppSurface>('loading');
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [instanceConfig, setInstanceConfig] = useState<Record<string, unknown>>({});
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  // ── SDK initialisation & context listener ────────────────────────────────
  useEffect(() => {
    console.log('[ChanelleOS] Initialising monday SDK…');

    // 1. Tell the SDK we are ready (required for iframe ↔ parent comms).
    //    `init()` was added in newer SDK versions; guard for v0.5.x which
    //    doesn't expose it.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (monday as any).init === 'function') {
      (monday as any).init();
      console.log('[ChanelleOS] monday.init() called');
    } else {
      console.log('[ChanelleOS] monday.init not available (SDK v0.5.x) — skipping');
    }

    // Helper: process incoming context exactly once per event
    const handleContext = (ctx: Record<string, unknown>) => {
      console.log('[ChanelleOS] Context received:', JSON.stringify(ctx).slice(0, 300));
      const detected = detectSurface(ctx);
      console.log('[ChanelleOS] Detected surface:', detected);
      setSurface(detected);

      // Push context into the Zustand store so every tab/widget can access it
      useAppStore.getState().setContext(ctx);

      // Theme
      const colorScheme = (ctx?.theme as string) ?? 'light';
      setTheme(colorScheme === 'dark' ? 'dark' : 'light');

      // For dashboard widget surfaces, grab the widget type from context
      if (detected === 'dashboard_widget') {
        const wId = ctx?.widgetId as string | undefined;
        setWidgetId(wId ?? null);
      }

      // Load board IDs so hooks can start fetching data
      const userObj = ctx?.user as Record<string, unknown> | undefined;
      const uid = (userObj?.id as string | number | undefined)?.toString();
      if (uid) {
        void initializeBoardIds(uid).then(() => {
          // Inject feed board IDs into the store alongside discovered boards
          const current = useAppStore.getState().boardIds;
          const updated = {
            ...current,
            task_feed: current.task_feed || FEED_BOARD_IDS.task_feed,
            data_sources: current.data_sources || FEED_BOARD_IDS.data_sources,
          };
          useAppStore.getState().setBoardIds(updated);

          // Auto-register known COS boards as data sources (idempotent)
          void registerAllKnownSources(updated).catch((err) => {
            console.warn('[ChanelleOS] Auto-registration of data sources failed:', err);
          });
        });
        void runLayoutMigration(uid);
      }
    };

    // 2. Subscribe to future context changes
    monday.listen('context', (res: { data: Record<string, unknown> }) => {
      handleContext(res.data);
    });

    // 3. Eagerly fetch the current context (in case the listen event
    //    already fired before we subscribed, or never fires at all)
    monday.get('context').then((res: { data: Record<string, unknown> }) => {
      console.log('[ChanelleOS] monday.get("context") resolved');
      if (res?.data) handleContext(res.data);
    }).catch((err: unknown) => {
      console.warn('[ChanelleOS] monday.get("context") failed:', err);
    });

    // 4. Settings listener
    monday.listen('settings', (res: { data: Record<string, unknown> }) => {
      setInstanceConfig(res.data ?? {});
    });

    monday.execute('valueCreatedForUser');

    // 5. Safety-net timeout: if we STILL have no context after 4 s,
    //    assume custom_object so the app is usable (local dev / edge cases)
    const fallbackTimer = setTimeout(() => {
      setSurface((prev) => {
        if (prev === 'loading') {
          console.warn(
            '[ChanelleOS] Context not received after 4 s — defaulting to custom_object',
          );
          // Also push a minimal context into the store so tab guards unblock
          const storeCtx = useAppStore.getState().context;
          if (!storeCtx) {
            useAppStore.getState().setContext({ _fallback: true });
          }
          return 'custom_object';
        }
        return prev;
      });
    }, 4000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  // ── Apply theme ──────────────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.toggle('dark-app-theme', theme === 'dark');
  }, [theme]);

  // ── Loading state ────────────────────────────────────────────────────────
  if (surface === 'loading') {
    return (
      <div className="app-loading">
        <div className="app-loading__spinner" aria-label="Loading Chanelle OS…" />
      </div>
    );
  }

  // ── Dashboard Widget surface ─────────────────────────────────────────────
  if (surface === 'dashboard_widget') {
    if (!widgetId) {
      return (
        <div className="app-error">
          <p>Widget ID not found. Please reinstall the widget.</p>
        </div>
      );
    }

    return (
      <Suspense fallback={<div className="widget widget--loading" aria-label="Loading…" />}>
        <WidgetRenderer
          widgetId={widgetId}
          instanceId={`dashboard_${widgetId}`}
          instanceConfig={instanceConfig}
          isEditMode={false}
          context={{}}
          onOpenConfig={() => undefined}
        />
      </Suspense>
    );
  }

  // ── Custom Object App surface (full tab shell) ────────────────────────────

  const ActiveTabComponent = TAB_COMPONENTS[activeTab] ?? HomeTab;

  return (
    <div className={`app-shell app-shell--${theme}`}>
      {/* NavBar reads activeTab / setActiveTab from the Zustand store directly */}
      <NavBar />

      <main className="app-main" role="main">
        <Suspense
          fallback={
            <div className="tab-loading" aria-label="Loading tab…">
              <div className="tab-loading__spinner" />
            </div>
          }
        >
          <ActiveTabComponent />
        </Suspense>
      </main>

      {/* Command Palette — always mounted, opened via CMD+K */}
      <CommandPalette />
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppInner />
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </QueryClientProvider>
  );
}
